import request from '@/utils/request';
import type {SystemSettings} from '@/types/system';

export interface SystemConfig {
  id: string;
  key: string;
  value: string;
  description: string;
  type: 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'JSON';
  createdAt: string;
  updatedAt: string;
}

export interface UpdateConfigData {
  value: string;
}

interface SystemLog {
  id: number;
  userId: number;
  username: string;
  action: string;
  module: string;
  description: string;
  ip: string;
  userAgent: string;
  createTime: string;
}

interface SystemLogQueryParams {
  page?: number;
  pageSize?: number;
  userId?: number;
  username?: string;
  action?: string;
  module?: string;
  startTime?: string;
  endTime?: string;
}

interface SystemLogListResponse {
  items: SystemLog[];
  total: number;
  page: number;
  pageSize: number;
}

interface SystemInfo {
  version: string;
  buildTime: string;
  javaVersion: string;
  osName: string;
  osVersion: string;
  serverTime: string;
  uptime: string;
  memoryUsage: {
    total: number;
    used: number;
    free: number;
  };
  diskUsage: {
    total: number;
    used: number;
    free: number;
  };
}

export const systemService = {
  getConfigs: async () => {
    const response = await request.get('/api/system/configs');
    return response;
  },

  getConfigByKey: async (key: string) => {
    const response = await request.get(`/api/system/configs/${key}`);
    return response;
  },

  updateConfig: async (key: string, data: UpdateConfigData) => {
    const response = await request.put(`/api/system/configs/${key}`, data);
    return response;
  },

  getSettings: async (): Promise<SystemSettings> => {
    const response = await request.get('/api/system/settings');
    return response;
  },

  updateSettings: async (data: Partial<SystemSettings>): Promise<SystemSettings> => {
    const response = await request.put('/api/system/settings', data);
    return response;
  },

  getLogs: async (params: SystemLogQueryParams = {}): Promise<SystemLogListResponse> => {
    const response = await request.get('/api/system/logs', { params });
    return response;
  },

  getLogById: async (id: number): Promise<SystemLog> => {
    const response = await request.get(`/api/system/logs/${id}`);
    return response;
  },

  clearLogs: async (): Promise<void> => {
    await request.delete('/api/system/logs');
  },

  getSystemInfo: async (): Promise<SystemInfo> => {
    const response = await request.get('/api/system/info');
    return response;
  },

  clearCache: async (): Promise<void> => {
    await request.post('/api/system/cache/clear');
  },

  backupDatabase: async () => {
    const response = await request.post('/api/system/backup', null, {
      responseType: 'blob',
    });
    return response;
  },

  restoreDatabase: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await request.post('/api/system/restore', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },
};
